import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import Input from '../components/UI/Input'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { organizationsAPI } from '../services/organizationsAPI'
import { usersAPI } from '../services/usersAPI'
import CreateOrganizationWizard from '../components/Modals/CreateOrganizationWizard'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import { Search, Plus, Building2, Users, Globe, MapPin, Calendar, Archive, Mail, RotateCcw } from 'lucide-react'

export default function Organizations() {
  const { user, hasPermission, isControlCenter } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [viewMode, setViewMode] = useState('grid') // 'grid' | 'list'
  const [showCreateWizard, setShowCreateWizard] = useState(false)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [organizationToRestore, setOrganizationToRestore] = useState(null)
  
  // Initialize activeTab from URL params or default to 'all'
  const tabFromUrl = searchParams.get('tab')
  const [activeTab, setActiveTab] = useState(tabFromUrl === 'archive' ? 'archive' : 'all')

  // Debounce search term to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, searchTerm.length === 1 ? 100 : 150) // Faster response for first character

    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset page when search term changes
  useEffect(() => {
    setPage(1)
  }, [debouncedSearchTerm])

  // Initialize tab from URL on mount
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab')
    if (tabFromUrl === 'archive') {
      setActiveTab('archive')
    }
  }, []) // Only run on mount

  // Auto-open create wizard if 'create' parameter is in URL
  useEffect(() => {
    const createParam = searchParams.get('create')
    if (createParam === 'true') {
      setShowCreateWizard(true)
      // Remove the 'create' parameter from URL after opening
      const newSearchParams = new URLSearchParams(searchParams)
      newSearchParams.delete('create')
      setSearchParams(newSearchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Reset page and status when tab changes
  useEffect(() => {
    setPage(1)
    if (activeTab === 'archive') {
      setStatus('archived')
      setSearchParams({ tab: 'archive' }, { replace: true })
    } else {
      setStatus('')
      setSearchParams({}, { replace: true })
    }
  }, [activeTab])

  // Determine the status filter based on active tab
  const getStatusFilter = () => {
    if (activeTab === 'archive') {
      return 'archived'
    }
    return status || null
  }

  // Query for organizations (when not on archive tab) - optimized caching
  const { data: orgsData, isLoading: orgsLoading, refetch } = useQuery(
    ['organizations', page, getStatusFilter(), debouncedSearchTerm, activeTab],
    () => {
      const statusFilter = getStatusFilter()
      if (debouncedSearchTerm.trim()) {
        return organizationsAPI.search({ q: debouncedSearchTerm, page, limit: 10, status: statusFilter })
      } else {
        return organizationsAPI.getAll({ page, limit: 10, status: statusFilter })
      }
    },
    { 
      enabled: isControlCenter() && activeTab !== 'archive',
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnMount: true, // Refetch stale data when component mounts (after edits)
    }
  )

  // Query for users from archived organizations (when on archive tab)
  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['archived-organization-users', page],
    () => usersAPI.getArchivedOrganizationUsers({ page, limit: 10 }),
    { 
      enabled: isControlCenter() && activeTab === 'archive',
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    }
  )

  // The API returns { success, data: { organizations, pagination } }
  const organizations = orgsData?.data?.data?.organizations || []
  const pagination = orgsData?.data?.data?.pagination || {}

  // The API returns { success, data: { users, pagination } }
  const archivedUsers = usersData?.data?.data?.users || []
  const usersPagination = usersData?.data?.data?.pagination || {}

  const isLoading = activeTab === 'archive' ? usersLoading : orgsLoading

  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleStatusChange = (e) => {
    setStatus(e.target.value)
    setPage(1)
  }

  const handleCreateOrganization = () => {
    setShowCreateWizard(true)
  }

  const handleCreateSuccess = () => {
    setShowCreateWizard(false)
    refetch()
  }

  const handleViewDetails = (id) => {
    navigate(`/organizations/${id}`)
  }

  const handleEdit = (id) => {
    navigate(`/organizations/${id}/edit`)
  }

  // Restore organization mutation
  const restoreOrganizationMutation = useMutation(
    (organizationId) => organizationsAPI.updateStatus(organizationId, 'active'),
    {
      onSuccess: (data, organizationId) => {
        // Invalidate and refetch queries
        queryClient.invalidateQueries(['organizations'])
        queryClient.invalidateQueries(['organization', organizationId])
        // Invalidate user queries for this organization so inactive users become active
        queryClient.invalidateQueries(['users', organizationId])
        queryClient.invalidateQueries(['archived-users', organizationId])
        queryClient.invalidateQueries(['archived-organization-users'])
        setShowRestoreModal(false)
        setOrganizationToRestore(null)
      },
      onError: (error) => {
        console.error('Restore organization error:', error)
        const errorMessage = error.response?.data?.message || 'Failed to restore organization. Please try again.'
        alert(errorMessage)
      }
    }
  )

  const handleRestoreClick = (organizationId, organizationName) => {
    setOrganizationToRestore({ id: organizationId, name: organizationName })
    setShowRestoreModal(true)
  }

  const handleRestoreConfirm = () => {
    if (organizationToRestore) {
      restoreOrganizationMutation.mutate(organizationToRestore.id)
    }
  }

  const handleRestoreCancel = () => {
    setShowRestoreModal(false)
    setOrganizationToRestore(null)
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' },
      archived: { color: 'bg-yellow-100 text-yellow-800', label: 'Archived' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  if (!isControlCenter()) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-4">Access Denied</h2>
        <p className="text-secondary-600">You don't have permission to view this page.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Organizations</h1>
          <p className="text-secondary-600 mt-1">Manage organizations and their settings</p>
        </div>
        <div className="flex items-center gap-2 mt-4 sm:mt-0">
          {activeTab !== 'archive' && (
            <div className="hidden sm:flex items-center rounded-md border border-secondary-300 overflow-hidden">
              <button
                className={`px-3 py-2 text-sm ${viewMode==='grid' ? 'bg-secondary-100 text-secondary-900' : 'text-secondary-700'}`}
                onClick={() => setViewMode('grid')}
              >Grid</button>
              <button
                className={`px-3 py-2 text-sm border-l border-secondary-300 ${viewMode==='list' ? 'bg-secondary-100 text-secondary-900' : 'text-secondary-700'}`}
                onClick={() => setViewMode('list')}
              >List</button>
            </div>
          )}
          {hasPermission('organizations.create') && activeTab !== 'archive' && (
            <Button onClick={handleCreateOrganization}>
              <Plus className="h-4 w-4 mr-2" />
              Create Organization
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('all')
              setSearchParams({}, { replace: true })
            }}
            className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'all'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-primary-600 hover:border-primary-300'
            }`}
          >
            <Building2 className="h-4 w-4 mr-2" />
            All Organizations
          </button>
          <button
            onClick={() => {
              setActiveTab('archive')
              setSearchParams({ tab: 'archive' }, { replace: true })
            }}
            className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'archive'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-secondary-500 hover:text-red-600 hover:border-red-300'
            }`}
          >
            <Archive className="h-4 w-4 mr-2" />
            Archive
          </button>
        </nav>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <Input
                  placeholder={activeTab === 'archive' ? "Search users..." : "Search organizations..."}
                  value={searchTerm}
                  onChange={handleSearch}
                  className="pl-10"
                />
              </div>
            </div>
            {activeTab !== 'archive' && (
              <div className="sm:w-48">
                <select
                  value={status}
                  onChange={handleStatusChange}
                  className="input w-full"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Content: Users List (Archive Tab) or Organizations List/Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : activeTab === 'archive' ? (
        // Archive Tab: Show Users from Archived Organizations
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-head">Email</th>
                  <th className="table-head">Organization Name</th>
                  <th className="table-head">Role</th>
                  <th className="table-head">Status</th>
                  <th className="table-head">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {archivedUsers.map((user) => (
                  <tr key={user.id} className="table-row">
                    <td className="table-cell">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-secondary-400" />
                        {user.email}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        <Building2 className="h-4 w-4 mr-2 text-secondary-400" />
                        <span className="font-medium">{user.organization_name || '-'}</span>
                      </div>
                    </td>
                    <td className="table-cell">{user.role || '-'}</td>
                    <td className="table-cell">{getStatusBadge(user.status)}</td>
                    <td className="table-cell">
                      <div className="flex gap-2">
                        {user.organization_id && hasPermission('organizations.update') && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRestoreClick(user.organization_id, user.organization_name)}
                            disabled={restoreOrganizationMutation.isLoading}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 hover:border-red-400"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {organizations.map((org) => (
            <Card key={org.id} className="hover:shadow-medium transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-lg bg-primary-100 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-secondary-900">{org.name}</h3>
                      <p className="text-sm text-secondary-600">{org.email}</p>
                    </div>
                  </div>
                  {getStatusBadge(org.status)}
                </div>
                <div className="space-y-3">
                  {org.website && (
                    <div className="flex items-center text-sm text-secondary-600">
                      <Globe className="h-4 w-4 mr-2" />
                      <a href={org.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary-600">{org.website}</a>
                    </div>
                  )}
                  {org.location && (
                    <div className="flex items-center text-sm text-secondary-600"><MapPin className="h-4 w-4 mr-2" />{org.location}</div>
                  )}
                  {org.industry && (
                    <div className="flex items-center text-sm text-secondary-600"><Building2 className="h-4 w-4 mr-2" />{org.industry}</div>
                  )}
                  <div className="flex items-center text-sm text-secondary-600"><Users className="h-4 w-4 mr-2" />{org.employee_count || 0} employees</div>
                  <div className="flex items-center text-sm text-secondary-600"><Calendar className="h-4 w-4 mr-2" />Created {new Date(org.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary-200">
                  <div className="flex space-x-2">
                    {hasPermission('organizations.update') && (
                      <Button variant="outline" size="sm" onClick={() => handleEdit(org.id)}>Edit</Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleViewDetails(org.id)}>View Details</Button>
                  </div>
                  <div className="text-xs text-secondary-500">{org.user_count || 0} users</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-head">Name</th>
                  <th className="table-head">Email</th>
                  <th className="table-head">Industry</th>
                  <th className="table-head">Employees</th>
                  <th className="table-head">Status</th>
                  <th className="table-head">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {organizations.map((org) => (
                  <tr key={org.id} className="table-row">
                    <td className="table-cell font-medium">{org.name}</td>
                    <td className="table-cell">{org.email}</td>
                    <td className="table-cell">{org.industry || '-'}</td>
                    <td className="table-cell">{org.employee_count || 0}</td>
                    <td className="table-cell">{getStatusBadge(org.status)}</td>
                    <td className="table-cell">
                      <div className="flex gap-2">
                        {hasPermission('organizations.update') && (
                          <Button variant="outline" size="sm" onClick={() => handleEdit(org.id)}>Edit</Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleViewDetails(org.id)}>View</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {((activeTab === 'archive' && archivedUsers.length === 0) || (activeTab !== 'archive' && organizations.length === 0)) && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            {activeTab === 'archive' ? (
              <Archive className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            ) : (
              <Building2 className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            )}
            <h3 className="text-lg font-medium text-secondary-900 mb-2">
              {debouncedSearchTerm 
                ? 'No results found' 
                : activeTab === 'archive' 
                  ? 'No users from archived organizations' 
                  : 'No organizations found'}
            </h3>
            <p className="text-secondary-600">
              {debouncedSearchTerm 
                ? `No results match your search for "${debouncedSearchTerm}". Try adjusting your search terms.`
                : activeTab === 'archive'
                  ? 'There are no users from archived organizations at this time.'
                  : 'Get started by creating your first organization.'
              }
            </p>
            {debouncedSearchTerm && (
              <Button 
                variant="outline" 
                onClick={() => setSearchTerm('')}
                className="mt-4"
              >
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {((activeTab === 'archive' && usersPagination.pages > 1) || (activeTab !== 'archive' && pagination.pages > 1)) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-secondary-700">
            {activeTab === 'archive' ? (
              <>Showing {((page - 1) * usersPagination.limit) + 1} to {Math.min(page * usersPagination.limit, usersPagination.total)} of {usersPagination.total} results</>
            ) : (
              <>Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} results</>
            )}
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page === (activeTab === 'archive' ? usersPagination.pages : pagination.pages)}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create Organization Wizard */}
      <CreateOrganizationWizard
        isOpen={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Restore Organization Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRestoreModal}
        onClose={handleRestoreCancel}
        onConfirm={handleRestoreConfirm}
        title="Restore Organization"
        message={`Are you sure you want to restore "${organizationToRestore?.name}"? This will change the organization status from archived to active.`}
        confirmText={restoreOrganizationMutation.isLoading ? "Restoring..." : "Restore"}
        cancelText="Cancel"
        type="warning"
      />
    </div>
  )
}
